import { useContext, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGetRoom } from 'app/hooks/data/core/admin/rooms/useRoom';
import { useGetHotel, useHotels } from 'app/hooks/data/hotels/useHotel';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import { useRoomFeatures } from 'app/hooks/data/rooms/useGetRoomFeatures';
import { useRoomCategories } from 'app/hooks/data/rooms/useRoomCategories';
import type { Room } from 'app/typings';

export const useRoomData = () => {
  const { isEditMode } = useContext(ViewEditModeContext);
  const { id: roomIdParam } = useParams<{ id: string }>();
  const { search } = useLocation();

  const roomId = useMemo(
    () => (roomIdParam ? parseInt(roomIdParam) : undefined),
    [roomIdParam]
  );
  const isCreatePage = !roomId;

  const hotelIdFromSearch = useMemo(() => {
    const parsedHotelId = parseInt(
      new URLSearchParams(search).get('hotelId') ?? ''
    );

    return Number.isNaN(parsedHotelId) ? undefined : parsedHotelId;
  }, [search]);

  const { data: fetchedRoom } = useGetRoom(roomIdParam);
  const { data: hotel } = useGetHotel(
    fetchedRoom?.hotelId ?? hotelIdFromSearch
  );
  const { data: hotels } = useHotels();
  const { data: roomFeatures } = useRoomFeatures();
  const { data: roomCategories } = useRoomCategories();
  const { data: bedCategories } = useBedCategories();

  const basicFeatureIds = useMemo(
    () =>
      roomFeatures
        ?.filter((feature) => feature.basic)
        .map((feature) => feature.id) ?? [],
    [roomFeatures]
  );

  const initialValues: Partial<Room> = useMemo(() => {
    if (isCreatePage) {
      return {
        beds: [{ included: true }],
        featureIds: basicFeatureIds,
        hotelId: hotelIdFromSearch,
      };
    }

    return fetchedRoom || {};
  }, [isCreatePage, basicFeatureIds, fetchedRoom, hotelIdFromSearch]);

  const isDataLoaded = useMemo(
    () =>
      !!hotels &&
      !!roomCategories &&
      !!bedCategories &&
      (isCreatePage || (fetchedRoom && hotel)),
    [hotels, roomCategories, bedCategories, isCreatePage, fetchedRoom, hotel]
  );

  const title = isCreatePage
    ? 'Create new room'
    : fetchedRoom?.name ||
      roomCategories?.find((rc) => rc.id === fetchedRoom?.categoryId)?.name ||
      'Room Details';

  const buttonSaveText = isCreatePage
    ? 'Create new room'
    : isEditMode
    ? 'Save'
    : 'Edit';

  return {
    roomId,
    isCreatePage,
    fetchedRoom,
    hotel,
    hotels,
    roomFeatures,
    roomCategories,
    bedCategories,
    basicFeatureIds,
    initialValues,
    isDataLoaded,
    buttonSaveText,
    title,
    hotelIdFromSearch,
  };
};
